import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, Input, Modal, Select, Spin, Tag } from 'antd';
import MatchSourceBadge from './MatchSourceBadge';
import {
  searchOrderGenieCustomers,
  searchOrderGenieProducts,
  searchOrderGenieShipToAddresses,
} from '../../services/orderXApi';
import {
  buildProductImageUrl,
  decodeHtmlEntities,
  extractOrderGenieCustomerSearchResults,
  extractOrderGenieProductSearchResults,
  extractOrderGenieShipToSearchResults,
  getErpMatchBadge,
  getMatchAiBadge,
  getUomMismatchBadge,
} from '../../pages/orderXV4Utils';

const ProductThumb = ({ src, alt }) => {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <div
        className="bg-light d-flex align-items-center justify-content-center rounded flex-shrink-0"
        style={{ width: 64, height: 64 }}
      >
        <span className="text-muted" style={{ fontSize: 10 }}>No image</span>
      </div>
    );
  }
  return (
    <div
      className="bg-light d-flex align-items-center justify-content-center rounded flex-shrink-0 overflow-hidden"
      style={{ width: 64, height: 64 }}
    >
      <img
        src={src}
        alt={alt}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        onError={() => setBroken(true)}
      />
    </div>
  );
};

const ProductOptionRow = ({
  product,
  price,
  priceLoading,
  code,
  name,
  selected,
  reasoning,
  aiBadge,
  uomMismatchBadge,
  erpBadge,
  sourceMatch,
  onUse,
  buttonLabel,
  unnamedPlaceholder,
  showImage = true,
}) => (
  <div className={`d-flex gap-3 align-items-center p-2 rounded ${selected ? 'border border-primary bg-light' : 'border'}`}>
    {showImage && <ProductThumb src={product?.imageSrc} alt={name} />}
    <div className="flex-grow-1" style={{ minWidth: 0 }}>
      <div className="fw-semibold text-truncate">{code}</div>
      <div className="text-muted small text-truncate">{name || unnamedPlaceholder}</div>
      {(sourceMatch || aiBadge || uomMismatchBadge || erpBadge || reasoning) && (
        <div className="text-muted small mt-1">
          {sourceMatch && <MatchSourceBadge match={sourceMatch} className="me-1" />}
          {aiBadge && <Tag color={aiBadge.color} className="me-1">{aiBadge.label}</Tag>}
          {uomMismatchBadge && (
            <Tag color={uomMismatchBadge.color} className="me-1">{uomMismatchBadge.label}</Tag>
          )}
          {erpBadge && <Tag color={erpBadge.color} className="me-1">{erpBadge.label}</Tag>}
          {reasoning && (
            <>
              <span className="fw-semibold">Match reason: </span>
              {reasoning}
            </>
          )}
        </div>
      )}
      {priceLoading && !Number.isFinite(Number(price)) ? (
        <div className="mt-1"><Spin size="small" /></div>
      ) : Number.isFinite(Number(price)) ? (
        <div className="fw-semibold small" style={{ color: '#0f6b3f' }}>${Number(price).toFixed(2)}</div>
      ) : null}
    </div>
    <Button size="small" type={selected ? 'default' : 'primary'} disabled={selected} onClick={onUse}>
      {selected ? 'Current match' : buttonLabel}
    </Button>
  </div>
);

// Mirrors the search_type values POST /api/v1/orderx/search/products documents
// as supported (order_genie.py's search_order_genie_products docstring) — the
// same AI Search modes available on the Products > AI Search admin page, so a
// reviewer isn't limited to hybrid semantic search when the AI-suggested
// matches miss the right product. Product-only — Customer/Ship-To search has
// no search_type concept (Story 1.3, Subtask 4.4).
const SEARCH_TYPES = [
  { value: 'hybrid_semantic', label: 'Hybrid (AI + keyword)' },
  { value: 'hybrid_with_filters', label: 'Hybrid with filters' },
  { value: 'exact_part_number', label: 'Exact part number' },
  { value: 'exact_manufacturer_part_number', label: 'Exact manufacturer part #' },
  { value: 'exact_part_or_mfr', label: 'Exact part or MFR #' },
  { value: 'exact_supplier_part_number', label: 'Exact supplier part #' },
  { value: 'exact_alt_part_number', label: 'Exact alt part #' },
  { value: 'exact_any_part_number', label: 'Exact match: part/MFR/alt/supplier # (any)' },
  { value: 'cpn', label: 'Customer part number (CPN)' },
];

// One entry per Match Type (Story 1.3, Subtask 4.2). The `product` entry's
// values reproduce today's exact literal strings/fields — this is a refactor,
// not a behavior change, for the default case. `idField` is the field on a
// normalized candidate that uniquely identifies it (see Story 1.3 Task 0 for
// why Ship-To uses `address_id`, not `customer_id`).
const MATCH_TYPE_CONFIG = {
  product: {
    idField: 'product_code',
    searchFn: searchOrderGenieProducts,
    normalizer: extractOrderGenieProductSearchResults,
    buildPayload: (query, searchType) => ({ query, search_type: searchType, page: 1, page_size: 12 }),
    hasSearchTypeDropdown: true,
    searchSectionTitle: 'Search for a different product',
    noResultsText: 'No products found',
    useButtonLabel: 'Use this product',
    unnamedPlaceholder: 'Unnamed product',
    showImage: true,
    showPrice: true,
    getPrimary: (c) => c?.product_code,
    getSecondary: (c) => c?.name,
  },
  customer: {
    idField: 'customer_id',
    searchFn: searchOrderGenieCustomers,
    normalizer: extractOrderGenieCustomerSearchResults,
    buildPayload: (query) => ({ query, page: 1, page_size: 12 }),
    hasSearchTypeDropdown: false,
    searchSectionTitle: 'Search for a different customer',
    noResultsText: 'No customers found',
    useButtonLabel: 'Use this customer',
    unnamedPlaceholder: 'Unnamed customer',
    showImage: false,
    showPrice: false,
    getPrimary: (c) => c?.name,
    getSecondary: (c) => c?.erp_customer_id || c?.external_customer_id || '',
  },
  shipto: {
    idField: 'address_id',
    searchFn: searchOrderGenieShipToAddresses,
    normalizer: extractOrderGenieShipToSearchResults,
    buildPayload: (query) => ({ query, page: 1, page_size: 12 }),
    hasSearchTypeDropdown: false,
    searchSectionTitle: 'Search for a different ship-to address',
    noResultsText: 'No ship-to addresses found',
    useButtonLabel: 'Use this address',
    unnamedPlaceholder: 'Unnamed address',
    showImage: false,
    showPrice: false,
    getPrimary: (c) => c?.name,
    getSecondary: (c) => [c?.address_line1, c?.city].filter(Boolean).join(', '),
  },
};

const EditMatchModal = ({
  open,
  onClose,
  item,
  itemMatches,
  selectedCode,
  erpPriceByProductCode,
  erpPriceLoadingByProductCode,
  loadErpPricesForCodes,
  productImageBaseUrl,
  onSelectMatch,
  onAddManualMatch,
  matchType = 'product',
}) => {
  const config = MATCH_TYPE_CONFIG[matchType] || MATCH_TYPE_CONFIG.product;
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('hybrid_semantic');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSearchType('hybrid_semantic');
    setSearchResults([]);
    setSearchError('');
    setHasSearched(false);
    // Suggested-matches (itemMatches) pricing preload is Product-only today
    // -- Customer/Ship-To have no ERP price concept, and no story has defined
    // an itemMatches shape for them yet (Story 1.4's job). See Subtask 4.5.
    if (matchType === 'product' && (itemMatches || []).length) {
      loadErpPricesForCodes((itemMatches || []).map((m) => m.product_code));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id, matchType]);

  const runSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearchError('');
    setHasSearched(true);
    try {
      const response = await config.searchFn(config.buildPayload(q, searchType));
      const results = config.normalizer(response);
      setSearchResults(results);
      if (matchType === 'product') {
        loadErpPricesForCodes(results.map((p) => p.product_code));
      }
    } catch (err) {
      setSearchError(err?.message || 'Search failed');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const partNumber = decodeHtmlEntities(item?.normalized_parts?.part_number || '');
  const description = decodeHtmlEntities(item?.normalized_parts?.description || '');

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={`Edit Match${partNumber ? ` — ${partNumber}` : ''}`}
      width={720}
      destroyOnClose
    >
      {description && <div className="text-muted small mb-3">{description}</div>}

      {matchType === 'product' && (itemMatches || []).length > 0 && (
        <div className="mb-4">
          <div className="fw-semibold small text-uppercase text-muted mb-2">Suggested matches</div>
          <div className="d-flex flex-column gap-2">
            {itemMatches.map((m) => (
              <ProductOptionRow
                key={m.product_code}
                product={{ imageSrc: buildProductImageUrl(m.match?.image_url, productImageBaseUrl) }}
                price={erpPriceByProductCode[m.product_code]}
                priceLoading={Boolean(erpPriceLoadingByProductCode?.[m.product_code])}
                code={m.product_code}
                name={decodeHtmlEntities(m.match?.name || '')}
                reasoning={m.reasoning ? decodeHtmlEntities(m.reasoning) : ''}
                aiBadge={getMatchAiBadge(m)}
                uomMismatchBadge={getUomMismatchBadge(m)}
                erpBadge={getErpMatchBadge(m)}
                sourceMatch={m}
                selected={m.product_code === selectedCode}
                buttonLabel="Select"
                onUse={() => {
                  onSelectMatch?.(m.product_code);
                  onClose();
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="fw-semibold small text-uppercase text-muted mb-2">{config.searchSectionTitle}</div>
        <div className="d-flex gap-2 flex-wrap">
          {config.hasSearchTypeDropdown && (
            <Select
              value={searchType}
              onChange={setSearchType}
              options={SEARCH_TYPES}
              style={{ width: 260 }}
              aria-label="Search mode"
              virtual={false}
            />
          )}
          <Input.Search
            className="flex-grow-1"
            style={{ minWidth: 240 }}
            placeholder="Search by part number, description, manufacturer..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onSearch={runSearch}
            loading={searching}
            enterButton="Search"
            allowClear
          />
        </div>
        <div className="mt-3">
          {searching && (
            <div className="text-center py-3"><Spin /></div>
          )}
          {!searching && searchError && <Alert type="error" showIcon message={searchError} />}
          {!searching && !searchError && hasSearched && searchResults.length === 0 && (
            <Empty description={config.noResultsText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
          {!searching && searchResults.length > 0 && (
            <div className="d-flex flex-column gap-2">
              {searchResults.map((c) => {
                const id = c[config.idField];
                return (
                  <ProductOptionRow
                    key={c._key}
                    product={{ imageSrc: config.showImage ? buildProductImageUrl(c.image_url, productImageBaseUrl) : '' }}
                    price={config.showPrice ? erpPriceByProductCode[id] : undefined}
                    priceLoading={config.showPrice ? Boolean(erpPriceLoadingByProductCode?.[id]) : false}
                    code={config.getPrimary(c)}
                    name={decodeHtmlEntities(config.getSecondary(c) || '')}
                    selected={id === selectedCode}
                    buttonLabel={config.useButtonLabel}
                    unnamedPlaceholder={config.unnamedPlaceholder}
                    showImage={config.showImage}
                    onUse={() => {
                      onAddManualMatch?.(c);
                      onClose();
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4">
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
};

export default EditMatchModal;
